import { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { shouldInviteInstall } from "./install-policy.js";
import {
  currentInstallPlatform,
  dismissInstallInvitation,
  initializeInstallDiscovery,
  markAppInstalled,
  requestAppInstall,
  useInstallState,
} from "./install-browser.js";
import { useInstallInvitationSpace } from "./use-install-invitation.js";
import "../styles/install-invitation.css";

export function InstallInvitation(): React.JSX.Element {
  const state = useInstallState();
  const space = useInstallInvitationSpace();
  const [help, setHelp] = useState(false);
  const platform = currentInstallPlatform();
  useEffect(() => {
    initializeInstallDiscovery();
    const open = () => setHelp(true);
    window.addEventListener("fairway:install-help", open);
    return () => window.removeEventListener("fairway:install-help", open);
  }, []);
  const show =
    space.ready &&
    shouldInviteInstall({
      ...state,
      platform,
      now: Date.now(),
      blocked: space.blocked || help,
    });
  const install = () => {
    if (!state.available) {
      setHelp(true);
      return;
    }
    void requestAppInstall().then((outcome) => {
      if (outcome === "unavailable") setHelp(true);
      else setHelp(false);
    });
  };
  const close = () => {
    setHelp(false);
    dismissInstallInvitation();
  };
  return (
    <>
      {show ? (
        <InstallBanner
          auth={space.auth}
          available={state.available}
          onInstall={install}
        />
      ) : null}
      <Dialog
        open={help && !state.standalone}
        onOpenChange={(open) => !open && close()}
      >
        <DialogContent className="fr-install-help fr-more-dialog">
          <InstallAppIcon />
          <DialogTitle>Add to home screen</DialogTitle>
          <DialogDescription>
            Open your clubhouse from your home screen.
          </DialogDescription>
          {state.available ? (
            <Button variant="gold-gradient" onClick={install}>
              Install app
            </Button>
          ) : (
            <InstallSteps platform={platform} />
          )}
          <Button variant="outline-gold" onClick={close}>
            Done
          </Button>
          <button
            className="fr-install-already"
            type="button"
            onClick={() => {
              markAppInstalled();
              setHelp(false);
            }}
          >
            Already added
          </button>
        </DialogContent>
      </Dialog>
    </>
  );
}

function InstallBanner(props: {
  auth: boolean;
  available: boolean;
  onInstall(): void;
}) {
  return (
    <aside
      className="fr-install-invite"
      data-auth={props.auth}
      aria-label="Add Fairway Rivalry to your home screen"
    >
      <img src="/apple-touch-icon.png" width="44" height="44" alt="" />
      <div>
        <strong>Your clubhouse, one tap away.</strong>
        <span>Add Fairway Rivalry to your home screen.</span>
      </div>
      <button
        type="button"
        className="fr-install-dismiss"
        aria-label="Not now"
        onClick={dismissInstallInvitation}
      >
        ×
      </button>
      <button
        type="button"
        className="fr-install-action"
        onClick={props.onInstall}
      >
        {props.available ? "Install app" : "Add to home screen"}
      </button>
    </aside>
  );
}

function InstallSteps({
  platform,
}: {
  platform: ReturnType<typeof currentInstallPlatform>;
}) {
  if (platform === "ios")
    return (
      <>
        <ol>
          <li>
            Open your browser’s <strong>Share</strong> menu.
          </li>
          <li>
            Choose <strong>Add to Home Screen</strong>.
          </li>
          <li>
            Keep <strong>Open as Web App</strong> on, if shown, then tap{" "}
            <strong>Add</strong>.
          </li>
        </ol>
        <p className="fr-install-hint">
          Can’t see it? In Safari, use Share → Edit Actions, or open this page
          in Safari from your current app.
        </p>
      </>
    );
  return (
    <ol>
      <li>Open your browser’s menu.</li>
      <li>
        Choose <strong>Install app</strong> or{" "}
        <strong>Add to Home Screen</strong>, then confirm.
      </li>
    </ol>
  );
}

function InstallAppIcon() {
  return (
    <img
      className="fr-more-install-icon"
      src="/apple-touch-icon.png"
      width="64"
      height="64"
      alt=""
    />
  );
}
